import React, {
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import * as Sentry from '@sentry/nextjs'
import { Button, toastManager } from '@systemeio/components'
import { ParseKeys } from 'i18next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import {
  AccessDenied,
  AccessDeniedReasonsEnum,
  getAccessDeniedText,
} from 'shared/errors/access-denied'
import useUser from 'shared/hooks/use-user'
import { CommentsList } from 'modules/comments/components'
import { CommentsTypeEnum } from 'modules/comments/types'
import { useCompleteCourseLecture } from '../hooks/use-complete-course-lecture'
import { useCourseData } from '../hooks/use-course-data'
import { useCourseLecture } from '../hooks/use-course-lecture'
import { useCourseLecturePreview } from '../hooks/use-course-lecture-preview'
import { useCourseMenu } from '../hooks/use-course-menu'
import { useFixAnchorsForIframe } from '../hooks/use-fix-anchors-for-iframe'
import { useFixFontsForIframe } from '../hooks/use-fix-fonts-for-iframe'
import useStepRouter from '../hooks/use-step-router'
import { CourseLectureId } from '../types/course-lecture-interface'
import { CourseQuizId } from '../types/course-quiz-interface'
import { CourseStepEnum } from '../types/course-step-type'
import { addPublicCustomClassNamePrefix } from '../utils/add-class-prefix'
import {
  applyHighlightStylesToIframe,
  removeHighlightStylesFromIframe,
} from '../utils/apply-highlight-styles-to-iframe'
import { resizeScrollSafeLectureIframeOnLoad, startFixLinksWatcher } from '../utils/iframe'
import { CourseContentSkeleton } from './course-content-skeleton'
import { CourseInfoScreenLayout } from './course-info-screens/course-info-screen-layout'
import { CourseNeedCompleteAssignmentScreen } from './course-info-screens/course-need-complete-assignment-screen'
import { CoursePurchaseModuleScreen } from './course-info-screens/course-purchase-module-screen'
import { CourseStepButton } from './course-state-button'
import CourseTracker from './course-tracker'
import { ScrollSafeIframe, setScrollSafeIframeWrapperHeight } from './scroll-safe-iframe'

interface CourseLectureProps {
  setOpenPassQuiz: React.Dispatch<
    React.SetStateAction<false | { blockingStep: CourseLectureId | CourseQuizId }>
  >
  lectureId: CourseLectureId
  isPreview: boolean
  previewHash?: string
}

const tagsWithRightClickDisabled = ['VIDEO', 'AUDIO']

export const CourseLecture = ({
  setOpenPassQuiz,
  lectureId,
  isPreview,
  previewHash,
}: CourseLectureProps): ReactNode => {
  const { t } = useTranslation()
  const router = useRouter()
  const { commentId, search } = router.query

  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const fixLinksCleanupRef = useRef<null | (() => void)>(null)

  const [iframeResized, setIframeResized] = useState(false)
  const [moduleLink, setModuleLink] = useState<string | null>(null)
  const [shouldSendLog, setShouldSendLog] = useState(true)
  const [accessError, setAccessError] = useState<ParseKeys | null>(null)
  const [blockingAssignmentId, setBlockingAssignmentId] = useState<CourseLectureId | undefined>(
    undefined,
  )

  const { step, onNextStep } = useStepRouter()
  const { user } = useUser()
  const { completeCourseLecture, isFetching } = useCompleteCourseLecture()
  const { courseData, mutate: mutateCourseData } = useCourseData()
  const { courseMenu, mutate } = useCourseMenu({ courseId: courseData?.id })

  const { courseLecture, isValidating } = useCourseLecture({
    courseLectureId: courseMenu && lectureId,
    searchQuery: search,
    shouldFetch: !isPreview,
    onError: error => {
      setShouldSendLog(false)

      if (!(error instanceof AccessDenied)) {
        toastManager.add({ type: 'error', title: t('global.error') })
        return
      }

      const data = error.response?.data
      if (typeof data !== 'object' || data === null || !('reason' in data)) {
        toastManager.add({ type: 'error', title: t('global.error') })
        return
      }

      const reason = data.reason as AccessDeniedReasonsEnum

      if (reason === AccessDeniedReasonsEnum.PurchaseRequired && 'module' in data) {
        setModuleLink(data.module as string)
      } else if (reason === AccessDeniedReasonsEnum.AssignmentNotSubmitted && 'lecture' in data) {
        setBlockingAssignmentId(data.lecture as CourseLectureId)
      } else if ('quiz' in data) {
        setOpenPassQuiz({ blockingStep: error.response?.data.quiz })
      } else {
        setAccessError(getAccessDeniedText(reason))
      }
    },
  })
  const { courseLecturePreview } = useCourseLecturePreview({
    hash: previewHash,
    shouldRender: isPreview,
  })

  const loggingEnabled =
    !!courseData?.lectureSessionLogging &&
    !isValidating &&
    !!(courseLecture?.html || courseLecturePreview) &&
    shouldSendLog
  const highlightEnabled = !!search

  const syncHighlightStyles = useCallback(
    (iframe: HTMLIFrameElement | null) => {
      if (!iframe) return

      if (!highlightEnabled) {
        removeHighlightStylesFromIframe(iframe)
        return
      }

      const rootStyles = getComputedStyle(document.documentElement)
      const highlightColor = rootStyles.getPropertyValue('--color-system-warning-500').trim()

      if (!highlightColor) {
        Sentry.captureMessage('Failed to read DS system warning color var from :root', {
          level: 'error',
        })
      }

      applyHighlightStylesToIframe(iframe, highlightColor || 'rgb(255 193 7)')
    },
    [highlightEnabled],
  )

  const onCompleteCourseLecture = async (): Promise<void> => {
    if (step?.id) {
      await completeCourseLecture(step.id)
      await mutate(
        data =>
          data?.map(module => ({
            ...module,
            lectures: module.lectures.map(lecture =>
              lecture.id === step?.id ? { ...lecture, completed: true } : lecture,
            ),
            quiz:
              module.quiz &&
              module.lectures.every(lecture => lecture.completed || lecture.id === step.id)
                ? { ...module.quiz, available: true }
                : module.quiz,
          })),
        false,
      )
      mutateCourseData()
      onNextStep()
    }
  }

  const disableRightClick = useCallback(() => {
    if (!iframeRef.current?.contentDocument?.body) return

    iframeRef.current.contentDocument.body.addEventListener('contextmenu', e => {
      const element = e.target as HTMLElement
      if (tagsWithRightClickDisabled.includes(element.tagName)) {
        e.preventDefault()
      }
    })
  }, [])

  useEffect(() => {
    if (courseData?.disableRightClick && iframeResized) {
      disableRightClick()
    }
  }, [courseData?.disableRightClick, disableRightClick, iframeResized])

  useEffect(() => {
    setModuleLink(null)
    setAccessError(null)
    setBlockingAssignmentId(undefined)
  }, [step?.id])

  useLayoutEffect(() => {
    const receiveMessage = (event: MessageEvent): void => {
      const shouldUpdateHeight =
        iframeRef.current &&
        iframeRef.current.contentDocument?.body &&
        event?.data?.type === 'lecture_content_height' &&
        event.data.height > 0

      if (shouldUpdateHeight) {
        const iframe = iframeRef.current!
        setScrollSafeIframeWrapperHeight(iframe, event.data.height)
        iframe.style.position = 'relative'
        iframe.style.opacity = '1'
        iframe.contentDocument!.body.style.overflowY = 'hidden'
        setIframeResized(true)
      }
    }

    window.addEventListener('message', receiveMessage)
    return () => window.removeEventListener('message', receiveMessage)
  }, [])

  useFixFontsForIframe({ iframeRef })
  useFixAnchorsForIframe(iframeRef, iframeResized)

  useLayoutEffect(() => {
    setIframeResized(false)
  }, [step?.id])

  useEffect(() => {
    return () => {
      // disconnect links watcher on unmount
      fixLinksCleanupRef.current?.()
    }
  }, [])

  useEffect(() => {
    syncHighlightStyles(iframeRef.current)
  }, [syncHighlightStyles])

  const handleLoadIframe = useCallback(
    (event: SyntheticEvent<HTMLIFrameElement>) => {
      const iframe = event.currentTarget

      const height = resizeScrollSafeLectureIframeOnLoad(iframe, setScrollSafeIframeWrapperHeight)
      setIframeResized(!!height)

      fixLinksCleanupRef.current?.()
      fixLinksCleanupRef.current = null

      fixLinksCleanupRef.current = startFixLinksWatcher(iframe)

      syncHighlightStyles(iframe)
    },
    [syncHighlightStyles],
  )

  const isAccessLocked =
    !!step &&
    step.type === CourseStepEnum.lecture &&
    !step.available &&
    (step.unavailabilityReason === 'not_included_in_partial_access' ||
      step.unavailabilityReason === 'not_enrolled' ||
      step.unavailabilityReason === 'purchase_required')

  const displayPurchaseScreen = !isValidating && !!step && (isAccessLocked || !!moduleLink)

  if (displayPurchaseScreen) return <CoursePurchaseModuleScreen moduleLink={moduleLink} />

  if (blockingAssignmentId !== undefined) {
    return <CourseNeedCompleteAssignmentScreen blockingAssignmentId={blockingAssignmentId} />
  }

  const isLoading = (isValidating || !(courseLecture?.html || courseLecturePreview)) && !accessError

  if (isLoading) return <CourseContentSkeleton />

  if (accessError)
    return (
      <CourseInfoScreenLayout
        text={<span className="whitespace-pre-line">{t(accessError)}</span>}
      />
    )

  return (
    <div
      data-testid="course-lecture-content"
      className={`relative w-full ${addPublicCustomClassNamePrefix('course-lecture__container')}`}
    >
      {loggingEnabled && <CourseTracker />}
      <div
        className={`relative w-full overflow-y-auto rounded-md bg-THEME-block-background [&>*::-webkit-scrollbar]:hidden`}
      >
        <ScrollSafeIframe
          srcDoc={courseLecture?.html || courseLecturePreview}
          ref={iframeRef}
          onLoad={handleLoadIframe}
        />
        {iframeResized && user && courseLecture?.showComments && (
          <div
            className={`px-3 py-4 lg:px-4 lg:py-5 ${addPublicCustomClassNamePrefix('course-lecture__comments-container')}`}
          >
            <CommentsList
              commentsType={CommentsTypeEnum.Page}
              onError={() =>
                toastManager.add({
                  type: 'error',
                  title: t('comments.error.error-loading-comments'),
                })
              }
              course={{ pageId: courseLecture?.pageId }}
              targetCommentId={commentId ? Number(commentId) : undefined}
            />
          </div>
        )}
        {!iframeResized && (
          <CourseContentSkeleton
            className={`z-40 ${addPublicCustomClassNamePrefix('course__content-skeleton--lecture')}`}
          />
        )}
      </div>
      {iframeResized && (
        <div className="mt-5 flex justify-center rounded-md bg-THEME-block-background p-6">
          {step?.completed ? (
            <CourseStepButton />
          ) : (
            <Button
              variant="primary"
              isLoading={isFetching}
              className={`bg-THEME-secondary! font-bold text-THEME-block-background! hover:bg-THEME-hover-secondary! hover:text-THEME-block-background! disabled:border-THEME-disabled! disabled:bg-THEME-disabled! ${addPublicCustomClassNamePrefix('course__button--mark-as-completed')}`}
              onClick={onCompleteCourseLecture}
            >
              {t('course.mark_step_as_completed_button_label')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
